import { gql } from '@/lib/api';

/**
 * Admin Console "Factus por tenant" — contrato GraphQL de la pestaña de facturación electrónica.
 * El API nunca devuelve `clientSecret`, `factusPassword` ni tokens: solo `hasClientSecret`,
 * `hasPassword` y `hasValidToken`. Los secretos solo viajan en `upsertPlatformTenantFactusConfig`
 * y únicamente cuando el admin los escribe.
 */
export type FactusEnvironment = 'SANDBOX' | 'PRODUCTION';
export type FactusStatus = 'PENDING' | 'ACTIVE' | 'SUSPENDED';

export interface PayrollElectronicConfig {
  environment: FactusEnvironment;
  status: FactusStatus;
  numberingRangeId: string | null;
  prefix: string | null;
  updatedAt: string;
}

export interface FactusConfig {
  tenantId: string;
  environment: FactusEnvironment;
  status: FactusStatus;
  clientId: string;
  factusUsername: string;
  hasClientSecret: boolean;
  hasPassword: boolean;
  hasValidToken: boolean;
  tokenExpiresAt: string | null;
  lastConnectionTestAt: string | null;
  lastConnectionTestOk: boolean | null;
  numberingRangeId: number | null;
  invoicePrefix: string | null;
  defaultTaxRate: number;
  nit: string;
  dv: string;
  companyName: string;
  municipalityCode: string;
  economicActivity: string | null;
  dianResolutionNumber: string | null;
  /** `AAAA-MM-DD`. */
  dianResolutionIssuedOn: string | null;
  dianRangeFrom: number | null;
  dianRangeTo: number | null;
  createdAt: string;
  updatedAt: string;
  payrollElectronic: PayrollElectronicConfig | null;
}

/** Secretos opcionales: ausentes = el API conserva los guardados (en creación son obligatorios). */
export interface UpsertFactusConfigInput {
  environment: FactusEnvironment;
  clientId: string;
  factusUsername: string;
  clientSecret?: string;
  factusPassword?: string;
  nit: string;
  verificationDigit: string;
  companyName: string;
  municipalityCode: string;
  economicActivity: string | null;
  invoicePrefix: string | null;
  numberingRangeId: number | null;
  defaultTaxRate: number;
  dianResolutionNumber: string | null;
  dianResolutionIssuedOn: string | null;
  dianRangeFrom: number | null;
  dianRangeTo: number | null;
}

export interface UpsertPayrollElectronicInput {
  environment: FactusEnvironment;
  numberingRangeId: string | null;
  prefix: string | null;
}

export interface FactusConnectionTestResult {
  ok: boolean;
  message: string;
  testedAt: string;
}

const PAYROLL_FIELDS = 'environment status numberingRangeId prefix updatedAt';
const CONFIG_FIELDS = `tenantId environment status clientId factusUsername hasClientSecret hasPassword
  hasValidToken tokenExpiresAt lastConnectionTestAt lastConnectionTestOk numberingRangeId invoicePrefix
  defaultTaxRate nit dv companyName municipalityCode economicActivity dianResolutionNumber
  dianResolutionIssuedOn dianRangeFrom dianRangeTo createdAt updatedAt
  payrollElectronic { ${PAYROLL_FIELDS} }`;

/** `platform.billing.factus.read`. `null` si el tenant aún no tiene configuración. */
export async function getTenantFactusConfig(tenantId: string): Promise<FactusConfig | null> {
  const data = await gql<{ platformTenantFactusConfig: FactusConfig | null }>(
    `query TenantFactusConfig($tenantId: ID!) {
      platformTenantFactusConfig(tenantId: $tenantId) { ${CONFIG_FIELDS} }
    }`,
    { tenantId },
  );
  return data.platformTenantFactusConfig;
}

/** `platform.billing.factus.write`. NIT/DV, municipio, rangos y tasa se validan otra vez en el API. */
export async function upsertTenantFactusConfig(
  tenantId: string,
  input: UpsertFactusConfigInput,
): Promise<FactusConfig> {
  const data = await gql<{ upsertPlatformTenantFactusConfig: FactusConfig }>(
    `mutation UpsertTenantFactusConfig($tenantId: ID!, $input: UpsertPlatformTenantFactusConfigInput!) {
      upsertPlatformTenantFactusConfig(tenantId: $tenantId, input: $input) { ${CONFIG_FIELDS} }
    }`,
    { tenantId, input },
  );
  return data.upsertPlatformTenantFactusConfig;
}

/** `platform.billing.factus.write`. Autentica de verdad contra Factus; el mensaje ya viene saneado. */
export async function testTenantFactusConnection(tenantId: string): Promise<FactusConnectionTestResult> {
  const data = await gql<{ testPlatformTenantFactusConnection: FactusConnectionTestResult }>(
    `mutation TestTenantFactusConnection($tenantId: ID!) {
      testPlatformTenantFactusConnection(tenantId: $tenantId) { ok message testedAt }
    }`,
    { tenantId },
  );
  return data.testPlatformTenantFactusConnection;
}

/** `platform.billing.factus.write`. Activar exige config completa y una prueba exitosa reciente. */
export async function setTenantFactusStatus(tenantId: string, status: FactusStatus): Promise<FactusConfig> {
  const data = await gql<{ setPlatformTenantFactusStatus: FactusConfig }>(
    `mutation SetTenantFactusStatus($tenantId: ID!, $status: PlatformFactusStatus!) {
      setPlatformTenantFactusStatus(tenantId: $tenantId, status: $status) { ${CONFIG_FIELDS} }
    }`,
    { tenantId, status },
  );
  return data.setPlatformTenantFactusStatus;
}

/** `platform.billing.factus.write`. Requiere la config de Factus ya guardada. */
export async function upsertTenantPayrollElectronicConfig(
  tenantId: string,
  input: UpsertPayrollElectronicInput,
): Promise<FactusConfig> {
  const data = await gql<{ upsertPlatformTenantPayrollElectronicConfig: FactusConfig }>(
    `mutation UpsertTenantPayrollElectronicConfig($tenantId: ID!, $input: UpsertPlatformTenantPayrollElectronicConfigInput!) {
      upsertPlatformTenantPayrollElectronicConfig(tenantId: $tenantId, input: $input) { ${CONFIG_FIELDS} }
    }`,
    { tenantId, input },
  );
  return data.upsertPlatformTenantPayrollElectronicConfig;
}

/** `platform.billing.factus.write`. Activar exige la facturación electrónica activa y el mismo ambiente. */
export async function setTenantPayrollElectronicStatus(
  tenantId: string,
  status: FactusStatus,
): Promise<FactusConfig> {
  const data = await gql<{ setPlatformTenantPayrollElectronicStatus: FactusConfig }>(
    `mutation SetTenantPayrollElectronicStatus($tenantId: ID!, $status: PlatformFactusStatus!) {
      setPlatformTenantPayrollElectronicStatus(tenantId: $tenantId, status: $status) { ${CONFIG_FIELDS} }
    }`,
    { tenantId, status },
  );
  return data.setPlatformTenantPayrollElectronicStatus;
}
